/* Region model and the aggregations every view shares. Pure functions over the Snapshot (D). */
import { d1 } from './format.js'

/* GIS sub-entities folded into their administrative regions (13-region views). */
export const FOLD = { 'جدة': 'مكة المكرمة', 'الطائف': 'مكة المكرمة', 'العاصمة المقدسة': 'مكة المكرمة', 'الاحساء': 'المنطقة الشرقية', 'حفر الباطن': 'المنطقة الشرقية' }
export const REGIONS = [
  { ar: 'تبوك', en: 'Tabuk' }, { ar: 'الجوف', en: 'Al-Jouf' }, { ar: 'الحدود الشمالية', en: 'Northern Borders' },
  { ar: 'حائل', en: 'Hail' }, { ar: 'المدينة المنورة', en: 'Madinah' }, { ar: 'القصيم', en: 'Qassim' },
  { ar: 'الرياض', en: 'Riyadh' }, { ar: 'المنطقة الشرقية', en: 'Eastern Province' }, { ar: 'مكة المكرمة', en: 'Makkah' },
  { ar: 'الباحة', en: 'Al-Bahah' }, { ar: 'عسير', en: 'Asir' }, { ar: 'نجران', en: 'Najran' }, { ar: 'جازان', en: 'Jazan' },
]
const CITY_EN = { 'جدة': 'Jeddah', 'الطائف': 'Taif', 'العاصمة المقدسة': 'Makkah City', 'الاحساء': 'Al-Ahsa', 'حفر الباطن': 'Hafr Al-Batin' }
const REG_EN = Object.fromEntries(REGIONS.map((r) => [r.ar, r.en]))
export const regionName = (ar, lang) => (lang === 'ar' ? ar : (REG_EN[ar] || CITY_EN[ar] || ar))
export const cityName = regionName
export const SAT_BUCKETS = ['VS', 'S', 'NU', 'D', 'VD']
export const SMALL_N = 1000

/** All-time per-region stats, folded to the 13 regions. */
export function foldedRegionStats(D) {
  const acc = {};
  (D.regionStats || []).forEach((r) => {
    const key = FOLD[r.region] || r.region
    const a = acc[key] || (acc[key] = { region: key, participants: 0, rated: 0, positive: 0, neutral: 0, negative: 0 })
    a.participants += r.participants || 0; a.rated += r.rated || 0
    a.positive += r.positive || 0; a.neutral += r.neutral || 0; a.negative += r.negative || 0
  })
  Object.values(acc).forEach((a) => { a.satPct = a.rated ? d1(a.positive / a.rated * 100) : null })
  return acc
}

/** Satisfaction buckets (optionally for one year) + per-region satisfaction, from the region×year×bucket matrix. */
export function satStats(D, year) {
  const g = { VS: 0, S: 0, NU: 0, D: 0, VD: 0 }; const raw = {};
  (D.regionYearMatrix || []).forEach(([r, y, b, c]) => {
    if (year && y !== year) return
    if (!(b in g)) return
    g[b] += c
    if (r) { const k = FOLD[r] || r; (raw[k] = raw[k] || { VS: 0, S: 0, NU: 0, D: 0, VD: 0 })[b] += c }
  })
  const tot = g.VS + g.S + g.NU + g.D + g.VD
  const regions = {}
  for (const k in raw) { const v = raw[k]; const t = v.VS + v.S + v.NU + v.D + v.VD; regions[k] = { rated: t, satPct: t ? d1((v.VS + v.S) / t * 100) : null } }
  return {
    vs: g.VS, s: g.S, nu: g.NU, d: g.D, vd: g.VD, total: tot,
    positivePct: tot ? d1((g.VS + g.S) / tot * 100) : 0,
    neutralPct: tot ? d1(g.NU / tot * 100) : 0,
    negativePct: tot ? d1((g.D + g.VD) / tot * 100) : 0,
    score: tot ? (5 * g.VS + 4 * g.S + 3 * g.NU + 2 * g.D + g.VD) / tot : 0,
    regions,
  }
}

/** Years with real volume (drives year chips and trims test rows). */
export function matrixYears(D) {
  const ys = new Set();
  (D.regionYearMatrix || []).forEach(([r, y, b, c]) => { if (y >= 2019 && y <= 2031 && c > 1000) ys.add(y) })
  return [...ys].sort()
}

/** Raw GIS entities (cities / amanahs, unfolded) ordered by rated volume. */
export function cityList(D) {
  const m = {};
  (D.regionYearMatrix || []).forEach(([r, y, b, n]) => { if (r && SAT_BUCKETS.includes(b)) m[r] = (m[r] || 0) + n })
  return Object.keys(m).sort((a, b) => m[b] - m[a])
}

/** Participants per year, summed from the monthly trend. */
export function participantsByYear(D) {
  const by = {};
  (D.trend || []).forEach((d) => { const y = +String(d.label).slice(0, 4); if (y) by[y] = (by[y] || 0) + (d.value || 0) })
  return by
}

/** Process completion rate from governance.byStatus. */
export function completion(D) {
  const by = Object.fromEntries(((D.governance || {}).byStatus || []).map((x) => [x.status, x.count]))
  const total = (by.Completed || 0) + (by.InProgress || 0) + (by.Draft || 0) + (by.Canceled || 0)
  return { by, total, done: by.Completed || 0, rate: total ? Math.round((by.Completed || 0) / total * 100) : 0 }
}

/** Yearly satisfaction series restricted to the reporting window. */
export const satByYear = (D) => (D.satisfactionByYear || []).filter((y) => y.year >= 2020 && y.year <= 2024).slice().sort((a, b) => a.year - b.year)

/** Region ranking for a year (null = all-time), with a minimum rated-answers threshold. */
export function regionRanking(D, year) {
  const base = foldedRegionStats(D)
  const yearRegs = satStats(D, year).regions
  return Object.keys(base).map((k) => {
    const y = yearRegs[k] || {}
    return { region: k, participants: base[k].participants, rated: y.rated || 0, satPct: y.satPct != null ? y.satPct : null }
  }).filter((r) => r.rated >= (year ? 3000 : 10000) && r.satPct != null).sort((a, b) => b.satPct - a.satPct)
}

/** Data coverage label, e.g. "2020 — 2024". */
export function coverage(D) {
  const yrs = (D.trend || []).filter((d) => d.value > 50).map((d) => +String(d.label).slice(0, 4))
  return yrs.length ? `${Math.min(...yrs)} — ${Math.max(...yrs)}` : '—'
}
